import React, { useState, useMemo } from 'react';
import { Edit2, MapPin, Phone, User, DollarSign, Clock, Image as ImageIcon, FileSpreadsheet, Printer, Search, Truck, X, MessageCircle, Navigation, Package, FileText, Zap } from 'lucide-react';
import ImageCarouselModal from './ImageCarouselModal';
import { DownloadExcelAnalytics, DownloadExcelProvinciaAnalytics } from './DownloadExcelAnalytics';

function DeliveryList({ deliveries, deliveriesProvince, orders, canEdit, onEdit, loading, selectedDate, setActiveComponent, expressDeliveries = [], userId }) {
  const [selectedImages, setSelectedImages] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deliveryType, setDeliveryType] = useState('lima'); // 'lima' | 'provincia' | 'express'
  const [expandedNotes, setExpandedNotes] = useState({});

  // Only Lima/Provincia use currentDeliveries — express has its own tab
  const currentDeliveries = deliveryType === 'lima' ? deliveries : deliveriesProvince;

  const toggleNote = (key) => {
    setExpandedNotes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredDeliveries = useMemo(() => {
    if (deliveryType === 'express') return [];
    if (!searchTerm.trim()) return currentDeliveries;

    const searchLower = searchTerm.toLowerCase().trim();
    return currentDeliveries.filter(delivery => {
      const idMatch    = delivery.id.toString().includes(searchLower);
      const nameMatch  = delivery.recipient_name?.toLowerCase().includes(searchLower);
      const phoneMatch = delivery.recipient_phone?.toLowerCase().includes(searchLower);

      if (deliveryType === 'provincia') {
        const dniMatch     = delivery.recipient_dni?.toLowerCase().includes(searchLower);
        const provinceMatch = delivery.province?.toLowerCase().includes(searchLower);
        const agencyMatch  = delivery.agency_address?.toLowerCase().includes(searchLower);
        return idMatch || nameMatch || phoneMatch || dniMatch || provinceMatch || agencyMatch;
      }
      return idMatch || nameMatch || phoneMatch;
    });
  }, [currentDeliveries, searchTerm, deliveryType]);

  function clearSearch() { setSearchTerm(''); }

  function handleWhatsAppClick(delivery) {
    const cleanPhone = delivery.recipient_phone?.replace(/[\s-]/g, '');
    const message = `¡Hola ${delivery.recipient_name}! 👋

Tu pedido está siendo procesado para entrega 📦

🚀 *Seguimiento de tu pedido*
Hemos contratado a Fast Max Courier para gestionar tu entrega.

Ingresa a este enlace único y seguro para:
✅ Ver la ubicación del motorizado en tiempo real
✅ Elegir tu horario aproximado de entrega
✅ Agregar detalles o referencias adicionales
✅ Enviar tu ubicación exacta

🔗 *Enlace de seguimiento:*
www.fastmaxlima.pe/#/entrega/${delivery.id}

💬 *¿Necesitas coordinar tu entrega?*
Comunícate directamente con la logística de Fast Max:
+51 977 802 104

¡Gracias por tu compra! 🌟`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/+51${cleanPhone}?text=${encodedMessage}`, '_blank');
  }

  function hasLocation(delivery) {
    return delivery.latitude && delivery.longitude;
  }

  function getStatusColor(status) {
    switch (status) {
      case 'pending':    return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'encamino':   return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'entregado':  return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelado':
      case 'rechazado':  return 'bg-red-100 text-red-700 border-red-200';
      default:           return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  }

  function getStatusLabel(status) {
    const labels = {
      pending:    'Pendiente',
      encamino:   'En Camino',
      entregado:  'Entregado',
      cancelado:  'Cancelado',
      rechazado:  'Rechazado',
      postergado: 'Postergado',
    };
    return labels[status] || status;
  }

  function handleDownloadExcel() {
    if (deliveryType === 'lima') {
      DownloadExcelAnalytics(filteredDeliveries, selectedDate, userId);
    } else {
      DownloadExcelProvinciaAnalytics(filteredDeliveries, selectedDate);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12">
        <div className="flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-sm text-slate-600 font-montserrat">Cargando entregas...</p>
        </div>
      </div>
    );
  }

  // Show section if any type has data
  const hasAnyDelivery = deliveries.length > 0 || deliveriesProvince.length > 0 || expressDeliveries.length > 0;

  return (
    <div className="space-y-4">

      {hasAnyDelivery && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

          {/* ── Header ── */}
          <div className="bg-brand-primary px-6 py-4">
            <div className="flex flex-col gap-4">

              {/* Tab selector */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => { setDeliveryType('lima'); setSearchTerm(''); }}
                  className={`px-4 py-2 rounded-lg font-montserrat font-semibold text-sm transition-all ${
                    deliveryType === 'lima'
                      ? 'bg-white text-brand-primary'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  Lima ({deliveries.length})
                </button>
                <button
                  onClick={() => { setDeliveryType('provincia'); setSearchTerm(''); }}
                  className={`px-4 py-2 rounded-lg font-montserrat font-semibold text-sm transition-all ${
                    deliveryType === 'provincia'
                      ? 'bg-white text-brand-primary'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  Provincia ({deliveriesProvince.length})
                </button>

                {/* Express tab — always shown when data exists */}
                {expressDeliveries.length > 0 && (
                  <button
                    onClick={() => { setDeliveryType('express'); setSearchTerm(''); }}
                    className={`px-4 py-2 rounded-lg font-montserrat font-semibold text-sm transition-all ${
                      deliveryType === 'express'
                        ? 'bg-white text-brand-green'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    Express ({expressDeliveries.length})
                  </button>
                )}
              </div>

              {/* Search + actions — only for Lima/Provincia */}
              {deliveryType !== 'express' && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <h3 className="text-lg font-extrabold text-white font-montserrat whitespace-nowrap">
                      Entregas {deliveryType === 'provincia' ? 'Provincia' : 'Lima'} ({filteredDeliveries.length})
                    </h3>
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={
                          deliveryType === 'provincia'
                            ? 'Buscar por ID, nombre, teléfono, DNI, provincia...'
                            : 'Buscar por ID, nombre o teléfono...'
                        }
                        className="w-full pl-9 pr-9 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 text-sm font-montserrat focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/30 transition-all"
                      />
                      {searchTerm && (
                        <button onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded transition-colors">
                          <X className="w-4 h-4 text-white" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button onClick={handleDownloadExcel} className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors group relative">
                      <FileSpreadsheet className="w-5 h-5" />
                      <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
                        Descargar Excel
                      </span>
                    </button>
                    {deliveryType === 'lima' && (
                      <button onClick={() => setActiveComponent('Print')} className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors group relative">
                        <Printer className="w-5 h-5" />
                        <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
                          Imprimir Rótulos
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Express header label */}
              {deliveryType === 'express' && (
                <h3 className="text-lg font-extrabold text-white font-montserrat">
                  Entregas Express ({expressDeliveries.length})
                </h3>
              )}

              {/* Search results info */}
              {searchTerm && deliveryType !== 'express' && (
                <div className="text-sm text-white/90 font-montserrat">
                  {filteredDeliveries.length === 0
                    ? <span>No se encontraron resultados para "{searchTerm}"</span>
                    : filteredDeliveries.length !== currentDeliveries.length
                      ? <span>Mostrando {filteredDeliveries.length} de {currentDeliveries.length} entregas</span>
                      : null
                  }
                </div>
              )}
            </div>
          </div>

          {/* ── Content ── */}
          <div className="divide-y divide-slate-200">

            {/* Lima / Provincia tab */}
            {deliveryType !== 'express' && (
              filteredDeliveries.length === 0 && searchTerm ? (
                <div className="p-12 text-center">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">No se encontraron entregas</h3>
                  <p className="text-sm text-slate-600 font-montserrat mb-4">No hay entregas que coincidan con "{searchTerm}"</p>
                  <button onClick={clearSearch} className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors font-montserrat text-sm">
                    Limpiar búsqueda
                  </button>
                </div>
              ) : filteredDeliveries.length === 0 ? (
                <div className="p-12 text-center">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                    No hay entregas {deliveryType === 'provincia' ? 'a provincia' : 'en Lima'}
                  </h3>
                  <p className="text-sm text-slate-600 font-montserrat">No se han registrado entregas para esta fecha</p>
                </div>
              ) : (
                filteredDeliveries.map(delivery => (
                  <div key={delivery.id} className="p-4 sm:p-6 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 space-y-3">

                        {/* Header Row */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold text-slate-500 font-montserrat">ID: {delivery.id}</span>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border font-montserrat ${getStatusColor(delivery.status)}`}>
                              {getStatusLabel(delivery.status)}
                            </span>
                            {deliveryType === 'provincia' && (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full border bg-purple-100 text-purple-700 border-purple-200 font-montserrat">
                                {delivery.province}
                              </span>
                            )}
                            {delivery.provider_id && (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200 font-montserrat">
                                Dropship
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            {deliveryType === 'lima' && (
                              <button onClick={() => handleWhatsAppClick(delivery)} className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors group relative">
                                <MessageCircle className="w-4 h-4" />
                                <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">Enviar WhatsApp</span>
                              </button>
                            )}
                            {deliveryType === 'lima' && (
                              <button
                                className={`p-2 rounded-lg transition-colors group relative ${hasLocation(delivery) ? 'bg-brand-green hover:bg-brand-green/80 text-white' : 'bg-slate-300 text-slate-500 cursor-default'}`}
                                disabled={!hasLocation(delivery)}
                              >
                                <Navigation className="w-4 h-4" />
                                <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
                                  {hasLocation(delivery) ? 'Ubicación disponible' : 'Sin ubicación'}
                                </span>
                              </button>
                            )}
                            {canEdit(delivery) && deliveryType === 'lima' ? (
                              <button onClick={() => onEdit(delivery)} className="p-2 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg transition-colors group relative">
                                <Edit2 className="w-4 h-4" />
                                <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">Editar</span>
                              </button>
                            ) : deliveryType === 'lima' ? (
                              <div className="p-2 bg-slate-200 text-slate-400 rounded-lg cursor-not-allowed group relative">
                                <Edit2 className="w-4 h-4" />
                                <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">No editable</span>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="text-slate-900 font-montserrat truncate">{delivery.recipient_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="text-slate-600 font-montserrat">{delivery.recipient_phone}</span>
                          </div>

                          {deliveryType === 'provincia' && (
                            <>
                              {delivery.recipient_dni && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-slate-500 font-montserrat">DNI:</span>
                                  <span className="text-slate-600 font-montserrat">{delivery.recipient_dni}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2 sm:col-span-2">
                                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <span className="text-slate-600 font-montserrat">{delivery.district}, {delivery.province}</span>
                              </div>
                              <div className="flex items-start gap-2 sm:col-span-2">
                                <Package className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-slate-500 font-montserrat">Agencia:</span>
                                  <span className="text-slate-600 font-montserrat text-sm">{delivery.agency_address}</span>
                                  {delivery.agency_pickup_code && (
                                    <span className="text-xs text-slate-500 font-montserrat mt-1">Código: {delivery.agency_pickup_code}</span>
                                  )}
                                </div>
                              </div>
                            </>
                          )}

                          {deliveryType === 'lima' && (
                            <div className="flex items-start gap-2 sm:col-span-2">
                              <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                              <span className="text-slate-600 font-montserrat truncate" title={`${delivery.address}, ${delivery.district}`}>
                                {delivery.address && delivery.address.length > 20
                                  ? `${delivery.address.substring(0, 20)}...`
                                  : delivery.address}, {delivery.district}
                              </span>
                            </div>
                          )}

                          {delivery.notes && (
                            <div className="flex items-start gap-2 sm:col-span-2">
                              <FileText className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                              <div className="flex flex-col gap-0.5 min-w-0">
                                <span className="text-xs font-semibold text-slate-500 font-montserrat">Notas:</span>
                                <span onClick={() => toggleNote(`notes_${delivery.id}`)} className="text-slate-600 font-montserrat text-sm cursor-pointer select-none">
                                  {expandedNotes[`notes_${delivery.id}`] ? delivery.notes : delivery.notes.length > 15 ? `${delivery.notes.substring(0, 15)}…` : delivery.notes}
                                </span>
                              </div>
                            </div>
                          )}

                          {delivery.notes_driver && (
                            <div className="flex items-start gap-2 sm:col-span-2">
                              <Truck className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                              <div className="flex flex-col gap-0.5 min-w-0">
                                <span className="text-xs font-semibold text-slate-500 font-montserrat">Notas driver:</span>
                                <span onClick={() => toggleNote(`notes_driver_${delivery.id}`)} className="text-slate-600 font-montserrat text-sm cursor-pointer select-none">
                                  {expandedNotes[`notes_driver_${delivery.id}`] ? delivery.notes_driver : delivery.notes_driver.length > 15 ? `${delivery.notes_driver.substring(0, 15)}…` : delivery.notes_driver}
                                </span>
                              </div>
                            </div>
                          )}

                          {delivery.product && (
                            <div className="flex items-start gap-2 sm:col-span-2">
                              <Package className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                              <span className="text-slate-600 font-montserrat text-sm">{delivery.product}</span>
                            </div>
                          )}

                          {(delivery.charge || delivery.charged || delivery.deposit_advance) && (
                            <div className="flex items-center gap-2 sm:col-span-2">
                              <DollarSign className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <div className="flex items-center gap-3 flex-wrap">
                                {delivery.charge && (
                                  <span className="text-slate-900 font-semibold font-montserrat">
                                    Por cobrar: S/ {parseFloat(delivery.charge).toFixed(2)}
                                  </span>
                                )}
                                {deliveryType === 'lima' && (delivery.charged || delivery.fastmax_transfer_amount) && (
                                  <span className="text-brand-green font-semibold font-montserrat">
                                    Cobrado: S/ {(parseFloat(delivery.charged || 0) + parseFloat(delivery.fastmax_transfer_amount || 0)).toFixed(2)}
                                  </span>
                                )}
                                {deliveryType === 'provincia' && delivery.deposit_advance && (
                                  <span className="text-blue-600 font-semibold font-montserrat">
                                    Anticipo: S/ {parseFloat(delivery.deposit_advance).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {delivery.arrival_time && deliveryType === 'lima' && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <span className="text-slate-600 font-montserrat text-xs">
                                {new Date(delivery.arrival_time).toLocaleString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )}

                          {delivery.images && delivery.images.length > 0 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setSelectedImages({ images: delivery.images, deliveryId: delivery.id, recipientName: delivery.recipient_name })}
                                className="flex items-center gap-2 px-3 py-1.5 bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary rounded-lg transition-colors"
                              >
                                <ImageIcon className="w-4 h-4" />
                                <span className="text-xs font-semibold font-montserrat">{delivery.images.length} {delivery.images.length === 1 ? 'imagen' : 'imágenes'}</span>
                              </button>
                            </div>
                          )}

                          {deliveryType === 'lima' && delivery.image_urls && delivery.image_urls.length > 0 && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setSelectedImages({ images: delivery.image_urls, deliveryId: delivery.id, recipientName: delivery.recipient_name })}
                                className="flex items-center gap-2 px-3 py-1.5 bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary rounded-lg transition-colors"
                              >
                                <ImageIcon className="w-4 h-4" />
                                <span className="text-xs font-semibold font-montserrat">{delivery.image_urls.length} {delivery.image_urls.length === 1 ? 'imagen' : 'imágenes'}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )
            )}

            {/* ── Express tab ── */}
            {deliveryType === 'express' && (
              expressDeliveries.length === 0 ? (
                <div className="p-12 text-center">
                  <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">No hay entregas express</h3>
                  <p className="text-sm text-slate-600 font-montserrat">No se han registrado servicios express para esta fecha</p>
                </div>
              ) : (
                expressDeliveries.map(ed => (
                  <div key={ed.id} className="p-4 sm:p-6 hover:bg-slate-50 transition-colors">
                    <div className="space-y-3">

                      {/* Header */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-slate-500 font-montserrat">ID: {ed.id}</span>
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border font-montserrat ${getStatusColor(ed.status)}`}>
                            {getStatusLabel(ed.status)}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full text-white font-montserrat ${ed.vehicle_type === 'bike' ? 'bg-brand-green' : 'bg-brand-primary'}`}>
                            {ed.vehicle_type === 'bike' ? '🏍 Moto' : '🚗 Auto'}
                          </span>
                        </div>
                        <span className="text-sm font-extrabold text-brand-green font-montserrat">
                          S/ {Number(ed.total_price).toFixed(2)}
                        </span>
                      </div>

                      {/* Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-slate-900 font-montserrat truncate">{ed.recipient_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-slate-600 font-montserrat">{ed.recipient_phone}</span>
                        </div>
                        <div className="flex items-start gap-2 sm:col-span-2">
                          <MapPin className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-[10px] font-semibold text-slate-400 font-montserrat uppercase">Recojo</span>
                            <p className="text-xs text-slate-600 font-montserrat">{ed.pickup_address}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 sm:col-span-2">
                          <Navigation className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-[10px] font-semibold text-slate-400 font-montserrat uppercase">Destino</span>
                            <p className="text-xs text-slate-600 font-montserrat">{ed.dropoff_address}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-xs text-slate-600 font-montserrat">
                            {ed.distance_km} km · S/ {Number(ed.rate_per_km).toFixed(2)}/km
                          </span>
                        </div>
                      </div>

                      {ed.items && ed.items.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Package className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-slate-600 font-montserrat">
                            {ed.items.map(i => `${i.quantity}x ${i.product_sku}`).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </div>
      )}

      {/* Orders Section */}
      {orders.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-brand-gold px-6 py-4">
            <h3 className="text-lg font-extrabold text-white font-montserrat">Recojos ({orders.length})</h3>
          </div>
          <div className="divide-y divide-slate-200">
            {orders.map(order => (
              <div key={order.id} className="p-4 sm:p-6 hover:bg-slate-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-500 font-montserrat">ID: {order.id}</span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border font-montserrat ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-900 font-montserrat truncate">{order.customer_name || 'N/A'}</span>
                      </div>
                      {order.telefono && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-slate-600 font-montserrat">{order.telefono}</span>
                        </div>
                      )}
                      {order.address && (
                        <div className="flex items-start gap-2 sm:col-span-2">
                          <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600 font-montserrat">{order.address.address}</span>
                        </div>
                      )}
                      {order.order_notes && (
                        <div className="sm:col-span-2">
                          <p className="text-xs text-slate-500 font-montserrat"><strong>Notas:</strong> {order.order_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedImages && (
        <ImageCarouselModal
          images={selectedImages.images}
          deliveryId={selectedImages.deliveryId}
          recipientName={selectedImages.recipientName}
          onClose={() => setSelectedImages(null)}
        />
      )}
    </div>
  );
}

export default DeliveryList;
